import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { DebounceInput } from "@/components/elements/Input/Input";
import { Select } from "@/components/elements/Select";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { AppRoutes } from "@/routes/config";
import { Link } from "react-router-dom";
import { black, divider } from "@/theme/colors";
import { Tooltip } from "@/components/elements/Tooltip";
import { IconErrorOutline, IconPlus } from "@/assets/icons";
import { TPagination } from "@/types/api";
import { useEnum } from "@/api/services/global/enum";

type TJobFilterForm = {
  keyword: string;
  companySpecificIndustryId: string;
  caseStatus: string;
};

type Props = {
  formValues: TJobFilterForm;
  onChangeFormValues: (key: keyof TJobFilterForm, val: unknown) => void;
  onSubmit: (vals: TJobFilterForm) => void;
  handleClearFilter: () => void;
  pagination?: TPagination;
};

export const JobListTop: FC<Props> = ({ formValues, onChangeFormValues, onSubmit, handleClearFilter, pagination }) => {
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { data: jobCaseFilterOptions } = useEnum("EJobCaseFilter");

  const isAllEmpty = useMemo(() => {
    return Boolean(!formValues.keyword && !formValues.companySpecificIndustryId && !formValues.caseStatus);
  }, [formValues]);

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  return (
    <Stack>
      <Stack mb={2}>
        <Typography variant="head20Bold">求人一覧</Typography>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="cap12Light">キーワード検索</Typography>
              <Tooltip title="以下から検索ができます。" content="求人企業名、求人タイトル、勤務地、求人番号">
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                  <IconErrorOutline width={15} height={15} color={black[80]} />
                </Box>
              </Tooltip>
            </Stack>
            <DebounceInput
              placeholder="入力してください"
              value={formValues.keyword}
              onChange={(e) => onChangeFormValues("keyword", e.target.value)}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">分野</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.companySpecificIndustryId}
              onChange={(value) => onChangeFormValues("companySpecificIndustryId", value || "")}
              options={companySpecificIndustryOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">マッチング状況</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.caseStatus}
              onChange={(value) => onChangeFormValues("caseStatus", value || "")}
              options={jobCaseFilterOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs="auto">
          <Stack spacing={1}>
            <Typography variant="cap12Light" sx={{ opacity: 0, lineHeight: "20px" }}>
              操作
            </Typography>
            <Grid container>
              <Grid item xs={6} display="flex" alignItems="center" justifyContent="center">
                <Box mr={2}>
                  <Button type="button" variant="text" disabled={isAllEmpty} onClick={handleClearFilter}>
                    <Typography variant="body14Semi">クリア</Typography>
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Button type="button" variant="outline" disabled={isAllEmpty} onClick={() => onSubmit(formValues)}>
                  <Typography variant="body14Semi">検索</Typography>
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ marginBottom: 2, marginTop: 2, color: divider.high, borderTop: "1px solid" }}></Box>

      <Box marginBottom={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body14Semi">
          全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
          件を表示
        </Typography>
        <Box display="flex" gap={2}>
          <Button component={Link} to={AppRoutes.jobCreate} size="md" startIcon={<IconPlus />}>
            <Typography variant="body14Semi">新規求人登録</Typography>
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};
